
import {
  ApiService,
  BarrelItemOutput, BarrelUpgrade,
  OutstandingPayment,
  PaymentOutput,
  SimpleBarrelStatusOutput,
} from "@/services/ApiService";
import { error } from "@/services/LoggerService";
import SimpleBarrel from "@/components/SimpleBarrel.vue";
import BarrelStatusHistoryRecord from "@/components/BarrelStatusHistoryRecord.vue";
import BarrelPaymentRecord from "@/components/BarrelPaymentRecord.vue";
import OutstandingPaymentRecord from "@/components/OutstandingPaymentRecord.vue";
import ItemTable from "@/components/ItemTable.vue";
import ConsigneeInfo from "@/components/ConsigneeInfo.vue";
import CustomerSidebar from "@/components/CustomerSidebar.vue";
import SnackbarNotifier from "@/components/SnackbarNotifier.vue";
import SnackbarNotifierMixin from "@/mixins/SnackbarNotifierMixins";
import ConfirmationModal from "@/components/ConfirmationModal.vue";
import ConfirmationModalMixin from "@/mixins/ConfirmationModalMixins";
import DisplayConsigneeUploadInfo from "@/components/DisplayConsigneeUploadInfo.vue";
import Modal from "@/components/Modal.vue";
import SupportCentre from "@/components/SupportCentre.vue";
import UpdateBarrelName from "@/components/UpdateBarrelName.vue";
import mixins from "vue-typed-mixins";
import dayjs from "dayjs";
import BarrelUpgradeRecord from "@/components/BarrelUpgradeRecord.vue";

type TableDisplay = "history" | "items" | "payments" | "upgrades";

export default mixins(ConfirmationModalMixin, SnackbarNotifierMixin).extend({
  name: "BarrelDetails",
  data() {
    return {
      barrelId: "" as string,
      customerName: "",
      customerPhoneNumber: "",
      barrel: {} as Record<string, any>,
      contents: [] as BarrelItemOutput[],
      statusHistoryRecords: [] as SimpleBarrelStatusOutput[],
      payments: [] as PaymentOutput[],
      outstandingPayments: [] as OutstandingPayment[],
      upgrades: [] as BarrelUpgrade[],
      tableDisplay: "items" as TableDisplay,
      loadingRetryPayment: false as boolean,
      showUploadInfoModal: false,
      passcode: "",
      fetching: false,
      fetchingConsigneeUploadInfo: false,
      consigneeUploadInfoBtnDisabled: false,
      requestShipmentLoading: false,
      showUpdateBarrelNameModal: false,
      nameBtnDisabled: false,
      operation: "",
      cancelLoading: false,
    };
  },
  components: {
    BarrelUpgradeRecord,
    SimpleBarrel,
    BarrelStatusHistoryRecord,
    BarrelPaymentRecord,
    OutstandingPaymentRecord,
    ItemTable,
    CustomerSidebar,
    SnackbarNotifier,
    ConfirmationModal,
    ConsigneeInfo,
    Modal,
    SupportCentre,
    DisplayConsigneeUploadInfo,
    UpdateBarrelName,
  },
  computed: {
    pybNumber(): string {
      return this.barrelId?.split("_")[0];
    },
    consigneeUploadInfoBtnTitle(): string {
      return this.fetchingConsigneeUploadInfo
        ? ""
        : "View Recipient Upload Info";
    },
    pageTitle(): string {
      const barrelActive = this.barrel.BarrelActive;
      const barrelClosed = this.barrel.BarrelClosed;
      const barrelName = this.barrel.BarrelName;
      if (barrelActive && !barrelClosed)
        return `${barrelName} - Current Barrel`;
      if (barrelActive && barrelClosed)
        return `${barrelName} - Processing Barrel`;
      if (!barrelActive && barrelClosed)
        return `${barrelName} - Delivered Barrel`;
      return `${barrelName ?? ""}`;
    },
    requestShipmentBtnClass() {
      const defaultClass = "btn btn-primary fw-bold px-6 py-5 w-100 mb-4 mt-6";
      if (this.requestShipmentLoading) return `${defaultClass} disabled`;
      return defaultClass;
    },
    hasOutstandingPayments(): boolean{
      if(!this.outstandingPayments){
        return false;
      }

      return this.outstandingPayments.length > 0;
    },
    hasUpgrades(): boolean{
      if(!this.upgrades){
        return false;
      }

      return this.upgrades.length > 0;
    }
    ,
    historyTab() {
      return "history";
    },
    itemsTab() {
      return "items";
    },
    paymentsTab() {
      return "payments";
    },
    upgradesTab(){
      return"upgrades";
    },
    outstandingPaymentsTab() {
      return "outstandingPayments";
    },
    showRetryPaymentButton(): boolean {
      return this.barrel.BarrelStatus === "Payment Pending";
    },
    enableRequestShipping(): boolean {
      return (
        this.barrel.BarrelStatus !== "Payment Pending" &&
        this.barrel.BarrelStatus != "Shipment Requested" &&
        this.barrel.BarrelStatus !== "Shipping Charge" &&
        this.barrel.BarrelStatus !== "Shipping Paid" &&
        !this.barrel.BarrelClosed
      );
    },
    enablePayShipping(): boolean {
      return this.outstandingPayments.some(p => p.Name == "Shipping Charge");
    },
    enablePayCustomsDuty(): boolean {
      return this.outstandingPayments.some(p => p.Name == "Customs Charge");
    },
    retryPaymentButtonClass() {
      const defautClass = "btn fw-bold px-6 py-5 w-100 mb-4 mt-6";
      if (this.loadingRetryPayment)
        return `${defautClass} btn-secondary disabled`;

      return `${defautClass} btn-primary`;
    },
    isBarrelOld(): boolean {
      if (!this.barrel?.Date) return true;
      const dayThreeDaysAgo = dayjs().subtract(3, "day");
      const dateOfBarrel = dayjs(this.barrel.Date);
      return dateOfBarrel.isBefore(dayThreeDaysAgo);
    },
  },
  methods: {
    closeUpdateBarrelModal() {
      this.showUpdateBarrelNameModal = false;
    },
    async successfulBarrelNameUpdate() {
      this.closeUpdateBarrelModal();
      this.$nextTick(async () => {
        await this.getDetails();
        this.showNotifier({
          message: "Successfully Updated Barrel Name",
          snackbarType: "success",
        });
      });
    },
    async retrieveConsigneeInfo() {
      try {
        this.fetchingConsigneeUploadInfo = true;
        this.consigneeUploadInfoBtnDisabled = true;
        this.showUploadInfoModal = true;
        this.fetchingConsigneeUploadInfo = false;
        this.consigneeUploadInfoBtnDisabled = false;
      } catch (err) {
        error(err);
        this.fetchingConsigneeUploadInfo = false;
        this.consigneeUploadInfoBtnDisabled = false;
        this.showNotifier({
          message: "Failed To Retrieve Passcode",
          snackbarType: "error",
        });
      }
    },
    getConsigneeName(consignee: any) {
      return `${consignee.FirstName} ${consignee.LastName}`;
    },
    formatAddress(address: any) {
      let addrString = `${address.Line1}, `;
      if (address.Line2) addrString += `${address.Line2}, `;
      addrString += `\n${address.Town}, ${address.Parish},\n ${address.Country}`;
      return addrString;
    },
    requestShipmentConfirmation() {
      this.showConfirmationModal({
        title: "Request Shipment",
        message:
          "<p>Are you sure you want to request this barrel to be shipped?</p><strong>Once requested, packing will commence</strong>.",
      });
      this.operation = "RequestShipment";
    },
    async requestShipment() {
      try {
        this.startConfirmationLoading();
        this.requestShipmentLoading = true;
        let result = await ApiService.Instance.setBarrelStatus({
          status: "shipment_request",
          barrelId: this.barrelId,
        });
        this.stopConfirmationLoading();
        this.requestShipmentLoading = false;
        if (result) setTimeout(async () => await this.getDetails(), 1000);
      } catch (e) {
        this.stopConfirmationLoading();
        this.requestShipmentLoading = false;
        error(e);
      }
    },
    async retryPayment() {
      try {
        this.loadingRetryPayment = true;
        this.startConfirmationLoading();
        const link = await ApiService.Instance.retryOrderBarrelPayment({
          barrelId: this.barrelId,
        });
        if (!link) {
          this.showNotifier({
            message: `Unable To Proceed To Payment Portal`,
            snackbarType: "error",
          });
          return;
        }
        window.location.href = link.paynowproRedirectUrl;
        this.loadingRetryPayment = false;
        this.stopConfirmationLoading();
      } catch (e) {
        this.showNotifier({
          message: "Failed To Retry The Payment",
          snackbarType: "error",
        });
        this.stopConfirmationLoading();
        this.loadingRetryPayment = false;
        error(e);
      }
    },
    retryPaymentConfirmation() {
      this.showConfirmationModal({
        title: "Retry Payment",
        message: "Are you sure you want to proceed?",
        confirmButtonTitle: "Yes",
      });
      this.operation = "RetryPayment";
    },
    editBarrelDetails() {
      this.$router.push({
        name: "Order",
        params: { barrelId: this.barrelId },
      });
    },
    async getDetails() {
      try {
        if (!this.barrelId) return;
        this.fetching = true;
        let temp = this.barrelId;
        this.barrelId = "";
        let result = await ApiService.Instance.barrelDetails({
          logicalBarrelId: temp,
        });
        this.fetching = false;
        this.barrelId = temp;
        this.barrel = result.barrel;
        this.contents = result.content;
        this.customerName = result.customerName;
        this.customerPhoneNumber = result.customerPhoneNumber;
        this.statusHistoryRecords = result.statusHistory;
        this.payments = result.payments;
        this.outstandingPayments = result.outstandingPayments;
        this.upgrades = result.upgrades;
      } catch (e) {
        this.fetching = false;
        this.showNotifier({
          message: "Failed To Load Barrel Details",
          snackbarType: "error",
        });
        error(e);
      }
    },
    async determineOperation() {
      if (this.operation === "RequestShipment") {
        await this.requestShipment();
        return;
      }

      if (this.operation === "RetryPayment") {
        await this.retryPayment();
        return;
      }

      this.showNotifier({
        message: "No operation set for this action",
        snackbarType: "error",
      });
    },
  },
  async beforeMount() {
    try {
      this.barrelId = this.$route.params.barrelId as string;
      window.sessionStorage.setItem("id", this.barrelId);
      await this.getDetails();

      if (this.barrel.BarrelName) {
        this.showNotifier({
          message: `Successfully Load Barrel Info`,
          snackbarType: "success",
        });
      }
    } catch (e) {
      this.showNotifier({
        message: `Failed To Load Barrel Info`,
        snackbarType: "error",
      });
      error(e);
    }
  },
});
